import React, { useMemo, useState, useEffect } from 'react';
import CountUp from '../components/CountUp';
import { AlertCircle, Calendar, Clock, FileText, ArrowRight, CheckCircle2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, Btn, Badge, StatusBadge, Table, TR, TD } from '../components/UI';
import TodoWidget from '../components/TodoWidget';
import ActivityFeed from '../components/ActivityFeed';
import { fmtAmt, nowMonth, monthsRange, getDueDateInMonth, getMonthLabel, fetchRatesPKR, toPKR } from '../utils/helpers';
import { onPayroll } from '../utils/hr';

function getMonthData(store, ym) {
  const { salaryPayments=[], subscriptions=[], oneTimeExpenses=[], oneTimeRevenue=[], commissionPayments=[] } = store;
  const income     = oneTimeRevenue.filter(r=>r.date?.slice(0,7)===ym).reduce((s,r)=>s+Number(r.amount||0),0);
  const salaries   = salaryPayments.filter(p=>p.monthYear===ym&&p.type!=='onetime').reduce((s,p)=>s+Number(p.amount||0),0);
  const otSalaries = salaryPayments.filter(p=>p.monthYear===ym&&p.type==='onetime').reduce((s,p)=>s+Number(p.amount||0),0);
  const subs       = subscriptions.reduce((s,x)=>s+Number(x.amount||0),0);
  const otExp      = oneTimeExpenses.filter(e=>e.date?.slice(0,7)===ym).reduce((s,e)=>s+Number(e.amount||0),0);
  const comm       = commissionPayments.filter(p=>p.monthYear===ym).reduce((s,p)=>s+Number(p.pkrAmount||0),0);
  const totalExpenses = salaries+otSalaries+subs+otExp+comm;
  return { income, totalExpenses, salaries, otSalaries, subs, otExp, comm, net: income-totalExpenses };
}

const SPEND_COLORS = ['var(--accent)','#8FB08A','#C79A5B','#C08079','var(--text3)'];

const LTooltip = ({ active, payload, label }) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:12,fontFamily:'var(--mono)'}}>
      <p style={{color:'var(--text3)',marginBottom:6,fontWeight:600}}>{label}</p>
      {payload.map((p,i)=><p key={i} style={{color:'var(--text)',margin:'3px 0',fontWeight:500}}>{p.name}: {fmtAmt(p.value)}</p>)}
    </div>
  );
};

// hairline stat-grid cell (dark) — matches the mock composition. Defined OUTSIDE the
// page so it isn't remounted on every refresh (which would restart the count-up).
const StatCell = ({ label, value, sub }) => (
  <div className="fx-card" style={{ background: 'var(--bg)', transition: 'transform .25s cubic-bezier(.2,.8,.2,1)', padding: '24px 22px 22px', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, minHeight: 138 }}>
    <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text3)' }}>{label}</div>
    <div style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(24px,2.4vw,32px)', lineHeight: 1, letterSpacing: '-0.01em', color: 'var(--text)', fontVariantNumeric: 'lining-nums tabular-nums' }}><CountUp value={value} /></div>
    {sub && <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.04em', color: 'var(--text3)', marginTop: 'auto' }}>{sub}</div>}
  </div>
);

// A little sky beside the greeting: a turning sun in the morning, a sinking sun in the
// afternoon, a glowing moon with twinkling stars in the evening.
function SkyIcon({ hour }) {
  const gold = 'var(--accent)';
  if (hour < 12) return (
    <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
      <g className="sky-rays" stroke={gold} strokeWidth="1.5" strokeLinecap="round">
        {[0,45,90,135,180,225,270,315].map(a => <line key={a} x1="16" y1="3" x2="16" y2="7" transform={`rotate(${a} 16 16)`}/>)}
      </g>
      <circle cx="16" cy="16" r="6" fill={gold}/>
    </svg>
  );
  if (hour < 17) return (
    <svg width="34" height="30" viewBox="0 0 36 32" aria-hidden="true">
      <g className="sky-sunset"><path d="M8 22a10 10 0 0 1 20 0z" fill={gold}/></g>
      <line x1="3" y1="23" x2="33" y2="23" stroke="var(--text3)" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="9" y1="27" x2="27" y2="27" stroke="var(--text3)" strokeWidth="1" strokeLinecap="round" opacity=".6"/>
    </svg>
  );
  return (
    <svg width="32" height="30" viewBox="0 0 34 32" aria-hidden="true">
      <path className="sky-moon" d="M21 5a11 11 0 1 0 8 16A9 9 0 0 1 21 5z" fill="#EFEBE4"/>
      <circle className="sky-star" cx="6" cy="8" r="1.3" fill={gold}/>
      <circle className="sky-star" cx="11" cy="3.5" r="1" fill="#EFEBE4"/>
      <circle className="sky-star" cx="4" cy="17" r="1" fill={gold}/>
    </svg>
  );
}

export default function DashboardPage({ store, onNavigate, persist, empId = 'admin' }) {
  const { employees: allEmployees=[], salaryPayments=[], subscriptions=[], oneTimeExpenses=[], oneTimeRevenue=[], commissionPayments=[], invoices=[], clients=[] } = store;
  // Salary reminders/totals: only people on payroll (no agent / freelancer without a salary).
  const employees = allEmployees.filter(e => onPayroll(store, e));
  const ym     = nowMonth();
  const months = monthsRange(6);
  const current = useMemo(()=>getMonthData(store,ym),[store,ym]);
  const prevYm  = (()=>{ const d=new Date(); d.setMonth(d.getMonth()-1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
  const prev    = useMemo(()=>getMonthData(store,prevYm),[store,prevYm]);

  const cashflowData = months.map(m=>{ const d=getMonthData(store,m); return {name:getMonthLabel(m).split(' ')[0],income:d.income,expenses:d.totalExpenses,net:d.net}; });
  const expBreakdown = [
    {name:'Salaries',value:current.salaries+current.otSalaries},
    {name:'Subscriptions',value:current.subs},
    {name:'Commission',value:current.comm},
    {name:'Other',value:current.otExp},
  ].filter(x=>x.value>0);
  const spendTotal = expBreakdown.reduce((s,x)=>s+x.value,0);

  const outstanding = invoices
    .filter(i=>['sent','pending','generated'].includes(i.status)&&Number(i.amount||0)>0)
    .map(inv=>({...inv,client:clients.find(c=>c.id===inv.clientId)}))
    .sort((a,b)=>{ if(a.status==='pending'&&b.status!=='pending') return -1; if(b.status==='pending'&&a.status!=='pending') return 1; return (b.generatedDate||'').localeCompare(a.generatedDate||''); });
  const [rates, setRates] = useState(null);
  useEffect(() => { fetchRatesPKR().then(setRates).catch(()=>{}); }, []);
  const totalReceivables = outstanding.reduce((s,i) => s + toPKR(i.amount, i.currency, rates), 0);

  const pct = (a,b)=>{ if(!b) return null; const v=((a-b)/b*100).toFixed(1); return {val:v,up:Number(v)>=0}; };
  const incPct = pct(current.income,prev.income);
  const netPct = pct(current.net,prev.net);

  // Unpaid salaries this month
  const unpaidSalaries = employees.filter(e => !salaryPayments.some(p => p.empId===e.id && p.monthYear===ym));
  const unpaidSalaryTotal = unpaidSalaries.reduce((s,e)=>s+Number(e.amount||0),0);

  // Salaries due within the next 7 days (hero figure)
  const todayStr = new Date().toISOString().slice(0,10);
  const in7 = (()=>{ const d=new Date(); d.setDate(d.getDate()+7); return d.toISOString().slice(0,10); })();
  const weekDueTotal = unpaidSalaries.reduce((s,e)=>{ const due=getDueDateInMonth(e.dueDay,ym); return (due>=todayStr && due<=in7) ? s+Number(e.amount||0) : s; },0);
  const heroAmount = weekDueTotal > 0 ? weekDueTotal : unpaidSalaryTotal;

  const upcoming = [];
  employees.forEach(e=>{ const d=getDueDateInMonth(e.dueDay,ym); const paid=salaryPayments.some(p=>p.empId===e.id&&p.monthYear===ym); if(!paid) upcoming.push({label:`${e.name} salary`,amount:e.amount,date:d,type:'salary'}); });
  subscriptions.forEach(s=>upcoming.push({label:s.name,amount:s.amount,date:getDueDateInMonth(s.dueDay,ym),type:'subscription'}));
  upcoming.sort((a,b)=>a.date.localeCompare(b.date));

  // ── Action centre ──
  const actionItems = useMemo(() => {
    const items = [];
    if (unpaidSalaries.length > 0)
      items.push({ type:'salary', dot:'var(--orange)', icon:AlertCircle, label:`${unpaidSalaries.length} unpaid salar${unpaidSalaries.length>1?'ies':'y'}`, detail:unpaidSalaries.map(e=>e.name).slice(0,3).join(', '), amount:fmtAmt(unpaidSalaryTotal), cta:'RESOLVE', go:'finance' });
    const overdueInv = outstanding.filter(i => i.status==='pending' || i.status==='sent');
    if (overdueInv.length > 0)
      items.push({ type:'invoice', dot:'var(--blue)', icon:FileText, label:`${overdueInv.length} invoice${overdueInv.length>1?'s':''} awaiting payment`, detail:overdueInv.map(i=>i.client?.name||'—').slice(0,3).join(', '), amount:fmtAmt(overdueInv.reduce((s,i)=>s+toPKR(i.amount,i.currency,rates),0)), cta:'VIEW', go:'invoices' });
    const pendingLeave = (store.leaveRequests||[]).filter(r => r.status==='pending');
    if (pendingLeave.length > 0)
      items.push({ type:'leave', dot:'var(--role-video)', icon:Calendar, label:`${pendingLeave.length} pending leave request${pendingLeave.length>1?'s':''}`, detail:pendingLeave.map(r=>{ const e=employees.find(x=>x.id===r.empId); return e?.name||'Someone'; }).slice(0,3).join(', '), amount:'', cta:'APPROVE', go:'hr' });
    const doneStatuses = ['Completed','Reviewed','Ready for Review','Client Review'];
    const overdueTasks = (store.tasks||[]).filter(t => t.dueDate && t.dueDate < todayStr && !doneStatuses.includes(t.status));
    if (overdueTasks.length > 0)
      items.push({ type:'task', dot:'var(--red)', icon:Clock, label:`${overdueTasks.length} task${overdueTasks.length>1?'s':''} overdue`, detail:overdueTasks.map(t=>t.taskName).slice(0,3).join(', '), amount:'', cta:'VIEW', go:'tasks' });
    return items;
  }, [unpaidSalaries, unpaidSalaryTotal, outstanding, store.leaveRequests, store.tasks, employees, todayStr]);

  const onLeaveToday = useMemo(() => (store.leaveRequests||[]).filter(r => {
    if (r.status !== 'approved') return false;
    return todayStr >= r.date && todayStr <= (r.endDate || r.date);
  }).map(r => { const e = employees.find(x=>x.id===r.empId); return { name: e?.name || 'Someone', type: r.type }; }),
  [store.leaveRequests, employees, todayStr]);

  // WFH today (from permanent schedule)
  const wfhToday = useMemo(() => {
    const dow = new Date().getDay();
    return employees.filter(e => {
      const wfh = store.hr?.[e.id]?.wfhDefaultDays;
      return Array.isArray(wfh) && wfh[dow];
    }).map(e => e.name);
  }, [employees, store.hr]);

  const activeCount = employees.length - onLeaveToday.length;

  const go = id => onNavigate && onNavigate(id);
  const hr = new Date().getHours();
  const greeting = `Good ${hr < 12 ? 'morning' : hr < 17 ? 'afternoon' : 'evening'}.`;
  const clientReviewCount = (store.tasks || []).filter(t => t.status === 'Client Review').length;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24}}>

      {/* ── OVERVIEW HEADER ── */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',gap:20,flexWrap:'wrap'}}>
        <div>
          <div style={{fontFamily:'var(--mono)',fontSize:10,letterSpacing:'0.22em',textTransform:'uppercase',color:'var(--text3)',marginBottom:9}}><span style={{color:'var(--accent)'}}>1.0</span>&nbsp;&nbsp;Overview</div>
          <h1 className="str-greeting" style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:'clamp(26px,3.4vw,38px)',lineHeight:1.04,margin:0,color:'var(--text)',display:'flex',alignItems:'center',gap:14}}>
            <span className="type-in">{greeting.split('').map((ch,i)=><span key={i} style={{animationDelay:`${0.03*i}s`}}>{ch}</span>)}</span>
            <SkyIcon hour={hr}/>
          </h1>
        </div>
        <Btn variant="primary" onClick={()=>go('invoices')}>+ New invoice</Btn>
      </div>

      {/* ── STAT GRID (hairline-split, one inverted) ── */}
      <div className="stagger" style={{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:1,background:'var(--border)',border:'1px solid var(--border)'}}>
        {/* Revenue — matches the other dark cells */}
        <StatCell label="Revenue · This month" value={fmtAmt(current.income)} sub={incPct ? `${incPct.up?'▲':'▼'} ${Math.abs(incPct.val)}% vs last month` : 'no prior data'}/>
        <StatCell label="Outstanding" value={fmtAmt(totalReceivables)} sub={`${outstanding.length} invoices unpaid`}/>
        <StatCell label="Active clients" value={clients.length} sub="on the books"/>
        <StatCell label="In client review" value={clientReviewCount} sub="tasks awaiting sign-off"/>
      </div>

      {/* ── REVENUE CHART + RECENT INVOICES ── */}
      <div style={{display:'grid',gridTemplateColumns:'1.7fr 1fr',gap:1,background:'var(--border)',border:'1px solid var(--border)'}} className="grid-chart">
        <div style={{background:'var(--bg)',padding:'24px 24px 20px',display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:22,gap:12,flexWrap:'wrap'}}>
            <span style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--text)'}}>Cash flow</span>
            <div style={{display:'flex',gap:16,fontFamily:'var(--mono)',fontSize:8.5,letterSpacing:'0.12em',textTransform:'uppercase',color:'var(--text3)'}}>
              <span style={{display:'flex',alignItems:'center',gap:6}}><span style={{width:12,height:2,background:'#EFEBE4'}}/>Revenue</span>
              <span style={{display:'flex',alignItems:'center',gap:6}}><span style={{width:12,height:2,background:'var(--text3)'}}/>Expenses</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={cashflowData} margin={{top:5,right:6,left:0,bottom:0}}>
              <defs><linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="rgba(169,137,83,0.20)"/><stop offset="1" stopColor="rgba(169,137,83,0)"/></linearGradient></defs>
              <CartesianGrid stroke="rgba(239,235,228,.06)" vertical={false}/>
              <XAxis dataKey="name" tick={{fontSize:9,fontFamily:'var(--mono)',fill:'var(--text3)'}} axisLine={false} tickLine={false} dy={6}/>
              <YAxis tick={{fontSize:9,fontFamily:'var(--mono)',fill:'var(--text3)'}} axisLine={false} tickLine={false} width={38} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
              <Tooltip content={<LTooltip/>}/>
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke="var(--text3)" strokeWidth={1} strokeDasharray="4 4" fill="none" dot={false} animationDuration={900} animationEasing="ease-out"/>
              <Area type="monotone" dataKey="income" name="Revenue" stroke="#EFEBE4" strokeWidth={1.5} fill="url(#revfill)" dot={false} activeDot={{r:3,fill:'var(--accent)',stroke:'none'}} animationDuration={1100} animationEasing="ease-out"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div style={{background:'var(--bg)',padding:'22px'}}>
          <span style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--text)'}}>Recent invoices</span>
          <div style={{marginTop:16,display:'flex',flexDirection:'column'}}>
            {outstanding.slice(0,5).map((inv,i)=>(
              <div key={inv.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,padding:'13px 0',borderTop:i?'1px solid var(--border)':'none'}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{inv.client?.name||'—'}</div>
                  <div style={{fontFamily:'var(--mono)',fontSize:8.5,letterSpacing:'0.13em',textTransform:'uppercase',color:'var(--text3)',marginTop:3}}>{inv.monthYear||''}</div>
                </div>
                <div style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text2)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtAmt(inv.amount)}</div>
                <StatusBadge status={inv.status}/>
              </div>
            ))}
            {outstanding.length===0 && <div style={{color:'var(--text3)',fontSize:13,padding:'14px 0'}}>All invoices settled.</div>}
          </div>
        </div>
      </div>

      {/* ── ACTION CENTRE ── */}
      <Card>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'18px 22px',borderBottom:'1px solid var(--border)'}}>
          <span style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--text)'}}>Action Centre</span>
          {actionItems.length>0 && <span style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'0.13em',textTransform:'uppercase',color:'#C08079',border:'1px solid #C08079',padding:'4px 9px'}}>{actionItems.length} urgent</span>}
        </div>
        <div>
          {actionItems.length === 0
            ? <div style={{padding:'2rem 1.5rem',display:'flex',alignItems:'center',gap:10,color:'var(--text3)',fontSize:14}}><CheckCircle2 size={18} color="#8FB08A"/> All caught up — nothing needs attention.</div>
            : actionItems.map((item,i)=>{
                const Icon=item.icon;
                return (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'16px 22px',borderBottom:i<actionItems.length-1?'1px solid var(--border)':'none'}}>
                    <Icon size={16} color="var(--accent)" style={{flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,color:'var(--text)'}}>{item.label}</div>
                      {item.detail && <div style={{fontSize:12,color:'var(--text3)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginTop:2}}>{item.detail}</div>}
                    </div>
                    {item.amount && <div style={{fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',fontSize:12.5,color:'var(--text2)',flexShrink:0}}>{item.amount}</div>}
                    <button onClick={()=>go(item.go)} style={{background:'transparent',border:'none',color:'var(--accent)',fontFamily:'var(--mono)',fontSize:10,letterSpacing:'0.12em',textTransform:'uppercase',cursor:'pointer',display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
                      {item.cta} <ArrowRight size={12}/>
                    </button>
                  </div>
                );
              })
          }
        </div>
      </Card>

      {/* ── ACTIVE TODAY ── */}
      <Card style={{padding:'18px 24px',display:'flex',alignItems:'center',gap:20,flexWrap:'wrap'}}>
        <div style={{flexShrink:0}}>
          <div className="t-label">Active Today</div>
          <div style={{fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',fontSize:22,fontWeight:700,color:'var(--text)',marginTop:3}}>{activeCount}/{employees.length} <span style={{fontSize:13,fontWeight:500,color:'var(--text3)'}}>Staff</span></div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',flex:1}}>
          {wfhToday.map((n,i)=>(
            <span key={'w'+i} style={{display:'inline-flex',alignItems:'center',gap:6,background:'transparent',border:'1px solid var(--accent)',color:'var(--accent)',padding:'6px 12px',borderRadius:'var(--radius)',fontSize:12.5,fontWeight:600,fontFamily:'var(--mono)'}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:'var(--accent)'}}/>{n.split(' ')[0]} (WFH)
            </span>
          ))}
          {onLeaveToday.map((p,i)=>(
            <span key={'l'+i} style={{display:'inline-flex',alignItems:'center',gap:6,background:'transparent',border:'1px solid #C08079',color:'#C08079',padding:'6px 12px',borderRadius:'var(--radius)',fontSize:12.5,fontWeight:600,fontFamily:'var(--mono)'}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:'#C08079'}}/>{p.name.split(' ')[0]} (On Leave)
            </span>
          ))}
          {wfhToday.length===0 && onLeaveToday.length===0 && <span style={{fontSize:13,color:'var(--text3)'}}>Everyone in office today.</span>}
        </div>
      </Card>

      {/* ── LIVE ACTIVITY ── */}
      <ActivityFeed />

      {/* ── UPCOMING PAYMENTS ── */}
      <Card>
        <div style={{padding:'18px 22px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'baseline',gap:10}}>
          <span style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--text)'}}>Upcoming payments</span>
          <span style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'0.13em',textTransform:'uppercase',color:'var(--text3)'}}>This month</span>
        </div>
        {upcoming.length===0
          ? <div style={{padding:'2.5rem',textAlign:'center',color:'var(--text3)',fontSize:14}}>No upcoming payments.</div>
          : (() => { const rows = upcoming.slice(0,8); return rows.map((u,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,padding:'13px 22px',borderBottom:i<rows.length-1?'1px solid var(--border)':'none'}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,color:'var(--text)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.label}</div>
                  <div style={{fontFamily:'var(--mono)',fontSize:10,letterSpacing:'0.05em',textTransform:'uppercase',color:'var(--text3)',marginTop:3}}>Due {u.date.slice(8,10)}/{u.date.slice(5,7)} · {u.type}</div>
                </div>
                <div style={{fontFamily:'var(--mono)',fontSize:13.5,color:'#C08079',fontVariantNumeric:'lining-nums tabular-nums',flexShrink:0}}>{fmtAmt(u.amount)}</div>
              </div>
            )); })()
        }
      </Card>

      <TodoWidget store={store} persist={persist} empId={empId} />

    </div>
  );
}
